/**
 * STABILITY-CRITICAL: orquestra "Limpar e recarregar": salvaguardas -> snapshot da
 * sessão -> marcador -> reset do banco -> reload. Tudo antes do reset é abortável sem perda;
 * depois dele o marcador garante a retomada. Antes de alterar, leia
 * docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 */
import { buildDiscardPayload } from './safeguards'
import type { FlowUi, SafeguardOutcome } from './safeguards'
import type { FlowEventBuffer, RecordFn } from './flowEvents'
import { updateMarker, writeMarker } from './resetMarker'
import type { SessionSnapshot } from './sessionSnapshot'

export type CleanupDeps = {
  online(): boolean
  isSyncBusy(): boolean
  pauseLoop(): void
  resumeLoop(): void
  safeguards(a: {
    mode: 'limpeza'
    ui: FlowUi
    emit: FlowEventBuffer['emit']
  }): Promise<SafeguardOutcome>
  buffer: FlowEventBuffer
  record: RecordFn
  takeSnapshot(): Promise<SessionSnapshot>
  readSavedFarmIds(): string[]
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  resetDb(snapshot: SessionSnapshot): Promise<{ failedKeys: string[] }>
  clearCacheAndReload(): void
  now(): number
}

export type CleanupResult =
  | { success: true; resetError?: string }
  | { success: false; reason: string; message: string }

export async function runCleanup(opts: {
  ui: FlowUi
  deps: CleanupDeps
}): Promise<CleanupResult> {
  const { ui, deps: d } = opts
  const { buffer } = d
  const fail = async (
    reason: string,
    message: string,
  ): Promise<CleanupResult> => {
    d.resumeLoop()
    await buffer.flush(d.record) // nada foi apagado: os eventos viram eventos normais
    return { success: false, reason, message }
  }

  if (!d.online())
    return {
      success: false,
      reason: 'precondition_offline',
      message: 'Sem internet.',
    }
  if (d.isSyncBusy())
    return {
      success: false,
      reason: 'precondition_busy',
      message: 'Sincronização em andamento.',
    }

  buffer.emit({ level: 'info', category: 'sync', action: 'limpeza_started' })
  d.pauseLoop()
  try {
    const out = await d.safeguards({ mode: 'limpeza', ui, emit: buffer.emit })
    if (!out.ok) return await fail(out.reason, out.message)

    ui.setStep(3, 'Limpando os dados deste aparelho...')
    let snapshot: SessionSnapshot
    try {
      snapshot = await d.takeSnapshot()
    } catch (e) {
      return await fail(
        'snapshot_failed',
        e instanceof Error ? e.message : String(e),
      )
    }
    const savedFarmIds = d.readSavedFarmIds()
    if (out.mustConfirm) {
      buffer.emit({
        level: 'warn',
        category: 'sync',
        action: 'dlq_discarded_on_reset',
        payload: buildDiscardPayload('limpeza', out),
      })
    }

    // Marcador ANTES do reset: se não gravar, não apaga.
    const gravou = writeMarker(
      {
        v: 1,
        step: 'reset_started',
        startedAt: d.now(),
        userId: snapshot.userId,
        savedFarmIds,
        selectedFarmIds: null,
        ownEvents: buffer.snapshot(),
      },
      d.storage,
    )
    if (!gravou)
      return await fail(
        'marker_failed',
        'Não foi possível gravar o marcador de retomada.',
      )

    // --- a partir daqui o banco pode ser zerado ---
    let resetError: string | undefined
    try {
      const r = await d.resetDb(snapshot)
      buffer.emit({
        level: 'info',
        category: 'sync',
        action: 'limpeza_reset_done',
        payload: { failedKeys: r.failedKeys.length },
      })
      updateMarker(
        { step: 'reset_done', ownEvents: buffer.snapshot() },
        d.storage,
      )
    } catch (e) {
      resetError = e instanceof Error ? e.message : String(e)
      buffer.emit({
        level: 'error',
        category: 'sync',
        action: 'limpeza_reset_failed',
        payload: { message: resetError },
      })
      updateMarker({ ownEvents: buffer.snapshot() }, d.storage) // step segue 'reset_started': a retomada normaliza
    }
    ui.finishing()
    d.clearCacheAndReload()
    return resetError ? { success: true, resetError } : { success: true }
  } catch (e) {
    return await fail('error', e instanceof Error ? e.message : String(e))
  }
}
